import React, { useState } from 'react';
import api from '../services/api.ts';
import { useQuery } from 'react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { VesselsAPI, Vessel, VesselStatus } from '../services/vessels.ts';
import { formatCurrency } from '../utils/currency.ts';
import { formatStatus, getStatusColor } from '../utils/statusFormatter.ts';
import { ArrowLeftIcon, PencilIcon, ArrowRightOnRectangleIcon, DocumentTextIcon, PhotoIcon, ArrowDownTrayIcon, EyeIcon } from '@heroicons/react/24/outline';
import BoatIcon from '../components/icons/BoatIcon.tsx';
import ImageGallery from '../components/ImageGallery.tsx';

// Função utilitária para construir URLs do backend
const getBackendUrl = (path: string) => {
  const baseUrl = process.env.REACT_APP_API_URL || 'http://localhost:8080';
  return `${baseUrl}${path}`;
};

const VesselDetail: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<'details' | 'related' | 'attachments'>('details');
  const [showImageGallery, setShowImageGallery] = useState(false);
  const [galleryStartIndex, setGalleryStartIndex] = useState(0);

  const { data: vessel, isLoading, error } = useQuery<Vessel | null>(
    ['vessel', id],
    async () => {
      if (!id) return null;
      const response = await VesselsAPI.get(Number(id));
      
      // Processar attachments se existirem
      if (response.attachments && Array.isArray(response.attachments)) {
        response.attachments = response.attachments.map((attachment: any) => {
          // Garantir que o type exista, derivando do mimetype se necessário
          const type = attachment.type || (attachment.mimetype?.startsWith('image/') ? 'image' : 'document');
          
          return {
            ...attachment,
            type,
            preview:
              type === 'image' && attachment.url ? getBackendUrl(attachment.url) : undefined,
            file: new File([], attachment.name || 'file'),
          };
        });
      } else {
        response.attachments = [];
      }
      
      return response;
    }
  );

  const { data: relatedAssets } = useQuery<any[]>(
    ['related-vessel', id],
    async () => {
      if (!id) return [];
      const response = await api.get(`/vessels/${id}/related`);
      return response.data;
    },
    { enabled: !!id }
  );

  const { data: assetRelatedAssets } = useQuery<any[]>(
    ['related-assets', vessel?.assetId],
    async () => {
      if (!vessel?.assetId) return [];
      const response = await api.get(`/assets/${vessel.assetId}/related`);
      return response.data;
    },
    { enabled: !!vessel?.assetId }
  );

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-500"></div>
      </div>
    );
  }

  if (error || !vessel) {
    return <div className="p-8 text-center text-red-500">Erro ao carregar embarcação.</div>;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 space-y-8">
        <div className="flex items-center justify-between">
          <div>
            <button
              onClick={() => navigate('/vessels')}
              className="flex items-center text-gray-600 hover:text-gray-900 mb-4 transition-colors"
            >
              <ArrowLeftIcon className="h-5 w-5 mr-2" />
              Voltar para Embarcações
            </button>
            <h1 className="text-3xl font-bold text-gray-900 flex items-center">
              <BoatIcon className="h-8 w-8 mr-3 text-primary-600" />
              {vessel.make} {vessel.model}
            </h1>
          </div>
          <div />
        </div>

        {/* Tabs */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm mb-8">
          <div className="px-6 py-4 border-b border-gray-100">
            <nav className="-mb-px flex space-x-8">
              <button onClick={() => setActiveTab('details')} className={`py-2 px-1 border-b-2 font-medium text-sm ${activeTab === 'details' ? 'border-primary-500 text-primary-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`}>Detalhes</button>
              <button onClick={() => setActiveTab('related')} className={`py-2 px-1 border-b-2 font-medium text-sm ${activeTab === 'related' ? 'border-primary-500 text-primary-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`}>Ativos Relacionados</button>
              <button onClick={() => setActiveTab('attachments')} className={`py-2 px-1 border-b-2 font-medium text-sm ${activeTab === 'attachments' ? 'border-primary-500 text-primary-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`}>Anexos</button>
            </nav>
          </div>
        </div>

        {activeTab === 'details' && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2 bg-white rounded-2xl shadow-lg border border-gray-100 p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <p className="text-sm text-gray-600">Status</p>
                <span className={`mt-1 inline-flex px-3 py-1 rounded-full text-xs font-semibold border ${getStatusColor(vessel.status)}`}>
                  {formatStatus(vessel.status)}
                </span>
              </div>
            <div>
              <p className="text-sm text-gray-600">Registro</p>
              <p className="font-semibold text-gray-900">{vessel.registrationNumber}</p>
            </div>
            <div>
              <p className="text-sm text-gray-600">Categoria</p>
              <p className="font-semibold text-gray-900">{({ boat: 'Barco', yacht: 'Iate', sailboat: 'Veleiro', other: 'Outro' } as Record<string, string>)[vessel.category ?? ''] ?? 'Não informado'}</p>
            </div>
            <div>
              <p className="text-sm text-gray-600">Ano • Cor</p>
              <p className="font-semibold text-gray-900">{vessel.year} • {vessel.color}</p>
            </div>
            <div>
              <p className="text-sm text-gray-600">Valor de Compra</p>
              <p className="font-semibold text-gray-900">{formatCurrency(vessel.purchasePrice)}</p>
            </div>
            <div>
              <p className="text-sm text-gray-600">Valor de Venda</p>
              <p className="font-semibold text-green-700">{vessel.sellingPrice ? formatCurrency(vessel.sellingPrice) : '-'}</p>
            </div>
            <div>
              <p className="text-sm text-gray-600">Data de Compra</p>
              <p className="font-semibold text-gray-900">{new Date(vessel.purchaseDate).toLocaleDateString('pt-BR')}</p>
            </div>
            {vessel.soldDate && (
              <div>
                <p className="text-sm text-gray-600">Data de Venda</p>
                <p className="font-semibold text-gray-900">{new Date(vessel.soldDate).toLocaleDateString('pt-BR')}</p>
              </div>
            )}
              </div>
            </div>

            <div className="space-y-6">
              <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Ações Rápidas</h3>
                <div className="space-y-3">
                  <button onClick={() => navigate(`/edit-vessel/${vessel.id}`)} className="w-full bg-primary-600 text-white px-4 py-2 rounded-lg hover:bg-primary-700 transition-colors flex items-center justify-center">
                    <PencilIcon className="h-4 w-4 mr-2" /> Editar Embarcação
                  </button>
                  {vessel.status === VesselStatus.IN_STOCK && (
                    <button onClick={() => navigate(`/exchanges/new?originType=vessel&originId=${id}`)} className="w-full bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 transition-colors flex items-center justify-center">
                      Registrar permuta
                    </button>
                  )}
                  {vessel.status === VesselStatus.IN_STOCK && (
                    <button onClick={() => navigate(`/vessels/${vessel.id}/sale`)} className="w-full bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors flex items-center justify-center">
                      <ArrowRightOnRectangleIcon className="h-4 w-4 mr-2" /> Registrar Venda
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'attachments' && (
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-semibold text-gray-900">Anexos - Fotos e Documentos</h3>
              <div className="flex items-center space-x-4">
                <span className="text-sm text-gray-500">
                  {vessel.attachments?.length || 0} arquivo(s)
                </span>
                <button
                  onClick={() => navigate(`/edit-vessel/${id}`)}
                  className="text-primary-600 hover:text-primary-800 text-sm font-medium"
                >
                  Adicionar/Editar
                </button>
              </div>
            </div>

            {/* Galeria de Fotos */}
            <div className="mb-8">
              <h4 className="text-sm font-medium text-gray-700 mb-4 flex items-center">
                <PhotoIcon className="h-4 w-4 mr-2" />
                Galeria de Fotos
              </h4>
              {(vessel.attachments || []).filter((a: any) => a.type === 'image').length > 0 ? (
                <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
                  {(vessel.attachments || [])
                    .filter((a: any) => a.type === 'image')
                    .map((photo: any, index: number) => (
                      <div
                        key={photo.id}
                        className="group relative aspect-square rounded-lg overflow-hidden bg-gray-100 cursor-pointer"
                        onClick={() => {
                          setGalleryStartIndex(index);
                          setShowImageGallery(true);
                        }}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            setGalleryStartIndex(index);
                            setShowImageGallery(true);
                          }
                        }}
                      >
                        <img
                          src={photo.preview || (photo.url ? getBackendUrl(photo.url) : '')}
                          alt={photo.name}
                          className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-110"
                        />
                        <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-opacity flex items-center justify-center">
                          <EyeIcon className="h-8 w-8 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                        </div>
                      </div>
                    ))}
                </div>
              ) : (
                <p className="text-sm text-gray-500 italic">Nenhuma foto adicionada.</p>
              )}
            </div>

            {/* Lista de Documentos */}
            <div>
              <h4 className="text-sm font-medium text-gray-700 mb-4 flex items-center">
                <DocumentTextIcon className="h-4 w-4 mr-2" />
                Documentos
              </h4>
              {(vessel.attachments || []).filter((a: any) => a.type === 'document').length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {(vessel.attachments || [])
                    .filter((a: any) => a.type === 'document')
                    .map((doc: any) => (
                      <div
                        key={doc.id}
                        className="flex items-center p-4 border border-gray-200 rounded-lg hover:border-primary-300 transition-colors bg-gray-50"
                      >
                        <div className="flex-shrink-0">
                          <DocumentTextIcon className="h-8 w-8 text-gray-400" />
                        </div>
                        <div className="ml-4 flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-900 truncate" title={doc.name}>
                            {doc.name}
                          </p>
                          <p className="text-xs text-gray-500">
                            {(doc.size / 1024).toFixed(0)} KB •{' '}
                            {new Date().toLocaleDateString('pt-BR')}
                          </p>
                        </div>
                        <div className="ml-4 flex-shrink-0">
                          <button
                            onClick={() => {
                              if (doc.url) {
                                const link = document.createElement('a');
                                link.href = getBackendUrl(doc.url);
                                link.download = doc.name;
                                link.target = '_blank';
                                link.click();
                              }
                            }}
                            className="text-gray-400 hover:text-primary-600 transition-colors"
                            title="Baixar documento"
                          >
                            <ArrowDownTrayIcon className="h-5 w-5" />
                          </button>
                        </div>
                      </div>
                    ))}
                </div>
              ) : (
                <p className="text-sm text-gray-500 italic">Nenhum documento adicionado.</p>
              )}
            </div>

            {showImageGallery && (
              <ImageGallery
                images={(vessel.attachments || []).filter((a: any) => a.type === 'image')}
                onClose={() => setShowImageGallery(false)}
                initialIndex={galleryStartIndex}
              />
            )}
          </div>
        )}

        {activeTab === 'related' && (
          <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6">
            <h3 className="text-xl font-semibold text-gray-900 mb-6">Ativos Relacionados</h3>
            {(relatedAssets && relatedAssets.length > 0 ? relatedAssets : (assetRelatedAssets || [])).length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {(relatedAssets && relatedAssets.length > 0 ? relatedAssets : (assetRelatedAssets || [])).map((relatedAsset: any) => {
                  const getRelationshipLabel = () => {
                    if (relatedAsset.relationship === 'exchange_origin') return 'Cedidos na permuta';
                    if (relatedAsset.relationship === 'exchange_result') return 'Recebidos na permuta';
                    return 'Relacionado';
                  };
                  const getTypeLabel = () => {
                    if (relatedAsset.type === 'vehicle') return 'Veículo';
                    if (relatedAsset.type === 'aircraft') return 'Aeronave';
                    if (relatedAsset.type === 'vessel') return 'Embarcação';
                    if (relatedAsset.type === 'real_estate') return 'Imóvel';
                    return 'Patrimônio';
                  };
                  return (
                    <div key={relatedAsset.id} className="border border-gray-200 rounded-lg p-4">
                      <div className="flex items-center justify-between mb-2">
                        <h4 className="font-medium text-gray-900">{relatedAsset.description}</h4>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(relatedAsset.status)}`}>
                          {formatStatus(relatedAsset.status)}
                        </span>
                      </div>
                      <p className="text-xs text-gray-500 mb-2">Tipo: {getTypeLabel()}</p>
                      <p className="text-sm text-gray-600 mb-2">Valor: {formatCurrency(Number(relatedAsset.currentValue || 0))}</p>
                      <div className="flex items-center justify-between">
                        <p className="text-xs text-gray-500">Relacionamento: {getRelationshipLabel()}</p>
                        <button
                          onClick={() => {
                            if (relatedAsset.type === 'vehicle') navigate(`/vehicles/${relatedAsset.id}`);
                            else if (relatedAsset.type === 'aircraft') navigate(`/aircrafts/${relatedAsset.id}`);
                            else if (relatedAsset.type === 'vessel') navigate(`/vessels/${relatedAsset.id}`);
                            else navigate(`/assets/${relatedAsset.id}`);
                          }}
                          className="text-primary-600 hover:text-primary-800 text-sm font-medium flex items-center"
                        >
                          <DocumentTextIcon className="h-4 w-4 mr-1" /> Ver
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-gray-600">Nenhum ativo relacionado encontrado.</div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default VesselDetail;